import * as Sentry from "@sentry/browser";
import axios from "axios";
import Image from "next/image";
import * as Router from "next/router";
import React from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";

import Button from "@/components/Button";
import Input from "@/components/Input";
import Link from "@/components/Link";
import SelectSearch from "@/components/SelectSearch";
import TickInput from "@/components/TickInput";
import * as T from "@/components/Typography";
import * as UserContext from "@/components/UserContext";
import * as U from "@/components/Utils";
import config from "@/config.client";
import * as c from "@/constants";
import firebase from "@/firebase.client";
import * as u from "@/utils";
import countries from "@/utils/countries";
import * as gtag from "@/utils/gtag";
import Chance from "chance";

const COUNTRIES = countries.map((elm) => elm.Name);

export default function RegisterForm(props) {
  const router = Router.useRouter();
  const user = React.useContext(UserContext.Context);
  const [isLoading, setIsLoading] = React.useState(false);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState(props.email);
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [termsAgreed, setTermsAgreed] = React.useState(false);
  const [marketingAgreed, setMarketingAgreed] = React.useState(false);
  const [thirdPartyAgreed, setThirdPartyAgreed] = React.useState(false);
  const [errMessage, setErrMessage] = React.useState("");
  const [areFieldsFilled, setAreFieldsFilled] = React.useState(false);

  const [emailWarning, setEmailWarning] = React.useState("");
  const rfc2822_CompliantEmailRegex = new RegExp(
    /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/
  );

  React.useEffect(() => {
    setAreFieldsFilled(
      ![firstName, lastName, email, country, password].includes("")
    );
  }, [firstName, lastName, email, country, password]);

  React.useEffect(() => {
    if (router.query.signup_type) {
      u.sessionStorage.setItem(c.SIGNUP_TYPE, router.query.signup_type);
      if (user.id && !user.membership?.id) router.push("/digital/select-plan");
    } else {
      if (u.sessionStorage.getItem(c.SIGNUP_TYPE) !== "NFT")
        u.sessionStorage.removeItem(c.SIGNUP_TYPE);
      if (user.id) router.push("/home");
    }
  }, [router.query]);

  return (
    <S.Container>
      {isLoading && <S.FixScreen />}
      <S.Form novalidate onSubmit={onCreateAccount}>
        {!props.embeddedPage && (
          <>
            <TT.H2>Sign up to AllBright Virtual</TT.H2>
            <U.Space height={10} />
            <U.Space height={10} />
          </>
        )}
        <Input
          name="first-name"
          hint="First name *"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          name="last-name"
          hint="Last name *"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          name="email"
          hint="Email *"
          value={email}
          onChange={(e) => {
            setEmailWarning("");
            setEmail(e.target.value);
          }}
          onBlur={handleEmailBlur}
          type="email"
        />
        {emailWarning && (
          <TT.CMB data-cy="email-warning">{emailWarning}</TT.CMB>
        )}
        <Input
          name="phone-number"
          hint="Phone Number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
        <SelectSearch
          name="country"
          hint="Country *"
          value={country}
          onSelect={(e) => setCountry(e)}
          opts={COUNTRIES}
        />
        <Input
          name="password"
          hint="Password *"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
        />
        <S.Ticks>
          <TickInput
            name="terms"
            isTrue={termsAgreed}
            onChange={() => setTermsAgreed(!termsAgreed)}
          >
            <TT.CM>
              I agree to the AllBright{" "}
              <Link href="/terms-conditions" isTextAnimation>
                {" "}
                Terms of Use{" "}
              </Link>
            </TT.CM>
          </TickInput>
          <TickInput
            name="marketing"
            isTrue={marketingAgreed}
            onChange={() => setMarketingAgreed(!marketingAgreed)}
          >
            <TT.CM>
              I would like to receive relevant information, newsletters and
              opportunity updates from AllBright by email
            </TT.CM>
          </TickInput>
          <TickInput
            name="thirdParty"
            isTrue={thirdPartyAgreed}
            onChange={() => setThirdPartyAgreed(!thirdPartyAgreed)}
          >
            <TT.CM>
              I would like to receive news and special offers from carefully
              selected partners of AllBright by email
            </TT.CM>
          </TickInput>
        </S.Ticks>
        {errMessage && <TT.CMB>{errMessage}</TT.CMB>}
        <S.ButtonContainer>
          <S.Button
            onClick={onCreateAccount}
            isWide={!props.embeddedPage}
            showIcon
            isLoading={isLoading}
            isDisable={!termsAgreed || !areFieldsFilled}
            text={"Continue"}
            data-cy="register-submit"
          />
          {!config.IS_PROD && (
            <S.Button
              name={"RANDOM"}
              onClick={onGenRandom}
              isWide={!props.embeddedPage}
              data-cy="register-random"
            />
          )}
        </S.ButtonContainer>
        {!props.embeddedPage && (
          <TT.CS>
            <Link href="/auth/login" isTextAnimation>
              Already a member? Log in here.
            </Link>
          </TT.CS>
        )}
      </S.Form>
    </S.Container>
  );

  async function onGenRandom(e) {
    e.preventDefault();
    const inst = Chance();
    const first = inst.first({ gender: "female" });
    const last = inst.last({ nationality: "en" });
    const email = `${first}.${last}@example.com`.toLowerCase();
    const phone = "+44" + inst.phone({ country: "uk", mobile: true }).slice(1);
    const country = "United Kingdom";
    const password = "Correct123";

    ReactDOM.unstable_batchedUpdates(() => {
      setFirstName(first);
      setLastName(last);
      setEmail(email);
      setCountry(country);
      setPhoneNumber(phone);
      setPassword(password);
    });
  }

  async function handleEmailBlur(e) {
    e.preventDefault();

    if (!rfc2822_CompliantEmailRegex.test(email)) {
      setEmailWarning("Please enter a valid email");
      return;
    }

    try {
      const { data } = await axios.post("/api/email.validate", { email });
      if (!data.isValid)
        setEmailWarning(
          `Looks like there's a typo, please double check your email.`
        );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
    }
  }

  async function onCreateAccount(e) {
    e.preventDefault();
    setIsLoading(true);

    if (!areFieldsFilled) {
      setIsLoading(false);
      setErrMessage("Please fill in all above fields");
      return;
    }

    if (!rfc2822_CompliantEmailRegex.test(email)) {
      setIsLoading(false);
      setErrMessage("Please enter a valid email");
      return;
    }

    try {
      await createAccount();

      gtag.event({
        name: gtag.GA_EVENT.DIGITAL_REGISTRATION,
        category: "Digital Registration",
        label: "Contact",
        value: 1,
      });

      props.onSuccessUrl
        ? router.push(props.onSuccessUrl)
        : router.push("/digital/select-plan");
    } catch (err) {
      console.error(err);
      if (err.code === "auth/email-already-in-use") {
        setErrMessage(
          "Looks like you're already registered, please log in using the link below. If you have any further issues please contact us for assistance."
        );
      } else if (err.code?.startsWith("auth") && err.message) {
        setErrMessage(err?.message);
      } else {
        setErrMessage(
          "There was a problem registering you at this time. Please contact us for assistance."
        );
      }

      setIsLoading(false);
      Sentry.captureException(err);
    }
  }

  async function createAccount() {
    const authRes = await firebase
      .auth()
      .createUserWithEmailAndPassword(email, password);
    const extras = { displayName: `${firstName} ${lastName}` };
    await authRes.user.updateProfile(extras);

    const registrationType =
      u.sessionStorage.getItem(c.SIGNUP_TYPE) ?? "DIGITAL_FREE";

    const body = {
      accessToken: authRes.user.Aa,
      termsAgreed,
      marketingAgreed,
      thirdPartyAgreed,
      email,
      firstName,
      lastName,
      country,
      location: country,
      phoneNumber,
      registrationSource: "web",
      registrationType,
    };

    const url = "/api/user.register-for-digital";
    const { data } = await axios.post(url, body);
    u.localStorage.setItem(c.FEED_TOKEN, data.tokens.feed);
    u.localStorage.setItem(c.CHAT_TOKEN, data.tokens.chat);
    u.sessionStorage.setItem(c.IS_BOARDING, true);

    user.updateUserInfo(data.userInfo);
  }
}

const S = {};

S.Container = styled.div``;

S.Hero = styled.header`
  height: 280px;
  position: relative;
`;

S.Form = styled.form`
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  margin: 40px auto 80px;
  max-width: 666px;
  padding: 0 16px;
  @media ${u.device.mobile} {
    padding: 0 20px;
  }
`;

S.Ticks = styled.div`
  margin: 5px 0 18px;
`;

S.FixScreen = styled.div`
  background: rgba(250, 250, 250, 0.4);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 10;
  :hover {
    cursor: default;
  }
`;

S.ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

S.Button = styled(Button)`
  width: 170px;
`;

const SH = {};

SH.Image = styled(Image)`
  filter: brightness(0.9);
  object-fit: cover;
  z-index: -1;
`;

SH.Wrapper = styled.div`
  align-items: center;
  bottom: 60px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  left: 0;
  position: absolute;
  right: 0;
  text-align: center;
  top: 0;
  @media ${u.device.tablet} {
    bottom: 40px;
  }
`;

const SF = {};

SF.State = styled.div`
  margin: 5px 0 -10px;
  height: 50px;
  @media (min-width: 466px) {
    height: 30px;
    margin: 0;
  }
`;

const TT = {};

TT.CS = styled(T.CS)`
  font-weight: 700;
  opacity: 1;
  letter-spacing: 1px;
  margin-top: 10px;
  margin-bottom: 20px;
  text-align: center;
  text-transform: uppercase;
`;

TT.H2 = styled(T.H2)`
  font-weight: 700;
  text-align: center;
`;

TT.CMState = styled(T.CM)`
  align-items: center;
  display: flex;
  height: 10px;
  justify-content: center;
  opacity: 1;
  > * {
    margin: 0 5px;
  }
`;

TT.CM = styled(T.CM)`
  opacity: 1;
`;

TT.CMB = styled(T.CM)`
  margin-bottom: 20px;
  color: red;
`;
